import {AvatarUpload, Modal} from '@sollapay/ui/components';
import {useIsGuestSession} from '@sollapay/web-core';
import {useTranslation} from 'react-i18next';

import {getInitials} from '@/utils';

import {AccountProfileFields} from './components';
import {useAccountSettingsForm} from '../hooks';

import type {UserProfileDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface AccountSettingsModalProps {
  isOpen: boolean;
  profile?: UserProfileDTO;
  /** Avatar color seed (the owner's user id) — the parent already resolved it via `useCurrentUserIdentity`. */
  avatarSeed?: string;
  onClose: () => void;
}

export const AccountSettingsModal: FC<AccountSettingsModalProps> = ({
  isOpen,
  profile,
  avatarSeed,
  onClose
}) => {
  const {t} = useTranslation(['settings', 'common']);
  const isGuest = useIsGuestSession();
  const {
    form,
    handleSave,
    isSaving,
    avatarUrl,
    avatarError,
    hasAvatarChange,
    onAvatarFileSelect,
    onRemoveAvatar
  } = useAccountSettingsForm({isOpen, profile, mode: 'settings', onSuccess: onClose});
  const hasChanges = form.formState.isDirty || hasAvatarChange;

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDirty={hasChanges}
      headerAlign="start"
      title={t('accountModal.title')}
      description={t(isGuest ? 'accountModal.guestDescription' : 'accountModal.description')}
      primaryLabel={t('accountModal.save')}
      onPrimaryAction={() => void handleSave()}
      primaryDisabled={!hasChanges}
      primaryLoading={isSaving}
      secondaryLabel={t('common:cancel')}
      onSecondaryAction={onClose}
    >
      <div className="flex flex-col gap-5">
        <AvatarUpload
          src={avatarUrl}
          initials={getInitials(profile?.displayName)}
          seed={avatarSeed}
          isUploading={isSaving}
          error={avatarError}
          onFileSelect={onAvatarFileSelect}
          onRemove={onRemoveAvatar}
          titleLabel={t('accountModal.avatar.title')}
          uploadLabel={t('accountModal.avatar.upload')}
          dragLabel={t('accountModal.avatar.drag')}
          removeLabel={t('accountModal.avatar.remove')}
        />
        <AccountProfileFields form={form} email={profile?.email} />
      </div>
    </Modal>
  );
};
